// floating-panel.service.ts
import { ComponentRef, DestroyRef, inject, Injectable, InjectionToken, Injector, Signal } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Action, CeActionPanelComponent } from './ce-action-panel.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CeOverlayContainerService } from '../ce-overlay/ce-overlay-container.service';
import { Icons } from '../ce-icon/ICONLIB';

export type ActionPanelData = {
    helperText: Signal<string>;
    prefixIcon?: keyof Icons;
    isDisabled: Signal<boolean>;
    primaryAction: Action;
    secondaryAction: Action;
    moreActions: Action[];
};

export const ACTION_PANEL_DATA = new InjectionToken<ActionPanelData>('ACTION_PANEL_DATA');

@Injectable({
    providedIn: 'root',
})
export class CeActionPanelService {
    #lastOverlayRef: OverlayRef | null = null;
    #actionPanelComponentRef: ComponentRef<CeActionPanelComponent> | null = null;
    #destroyRef = inject(DestroyRef);
    #overlay = inject(Overlay);
    #injector = inject(Injector);
    #overlayContainerService = inject(CeOverlayContainerService);

    openPanel(data: ActionPanelData): OverlayRef {
        // If there is an existing panel, close it.
        if (this.#lastOverlayRef) {
            this.#lastOverlayRef.detach();
            this.#lastOverlayRef = null;
            // Delete all the divs with class action-panel-overlay-container from the DOM
            const existingContainers = document.querySelectorAll('.action-panel-overlay-container');
            existingContainers.forEach((container) => container.remove());
        }

        const customOverlayContainer = this.#overlayContainerService.createOverlayContainer('action-panel-overlay-container');

        const positionStrategy = this.#overlay.position().global().centerHorizontally();

        const overlayConfig = new OverlayConfig({
            hasBackdrop: false,
            positionStrategy,
            disposeOnNavigation: true,
        });

        this.#lastOverlayRef = this.#overlay.create(overlayConfig);

        const injectionTokens = new WeakMap();
        injectionTokens.set(ACTION_PANEL_DATA, data);
        const injector = Injector.create({
            providers: [{ provide: ACTION_PANEL_DATA, useValue: data }],
            parent: this.#injector,
        });

        const portal = new ComponentPortal(CeActionPanelComponent, null, injector);
        this.#actionPanelComponentRef = this.#lastOverlayRef.attach(portal);
        customOverlayContainer.appendChild(this.#lastOverlayRef.hostElement);

        // Subscribe to the close event of the action panel
        this.#actionPanelComponentRef.instance.actionPanelClosed.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
            this.detachPanel();
            this.#overlayContainerService.removeOverlayContainer(customOverlayContainer);
        });

        return this.#lastOverlayRef;
    }

    detachPanel() {
        if (this.#lastOverlayRef) {
            this.#lastOverlayRef.detach();
        }
    }

    closePanel() {
        if (this.#lastOverlayRef) {
            this.#actionPanelComponentRef?.instance.close();
        }
    }
}
